import PhoneInput, { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import { useMemo } from "react";

/**
 * PhoneInputField - A professional phone input component with country code selector.
 * 
 * Uses react-phone-number-input which provides:
 * - All 240+ countries with dial codes from Google's libphonenumber
 * - Country flag icons
 * - Searchable country dropdown
 * - Automatic phone number formatting
 * - Built-in validation
 * 
 * Custom labels show: "Country Name (ISO) +DialCode"
 * e.g., "India (IN) +91", "United States (US) +1"
 * 
 * @param {string} value - Phone number value in E.164 format (e.g., "+919876543210")
 * @param {function} onChange - Callback when phone number changes
 * @param {function} onBlur - Callback when phone input loses focus
 * @param {string} defaultCountry - Default country code (e.g., "IN")
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} required - Whether the field is required
 * @param {boolean} hasError - Whether to display error styling
 * @param {string} id - Input element ID
 * @param {string} name - Input element name
 */
export default function PhoneInputField({ 
  value, 
  onChange, 
  onBlur,
  defaultCountry = "IN", 
  placeholder = "Enter phone number",
  required = false,
  hasError = false,
  id = "phone",
  name = "phone"
}) {
  // Build custom labels that include country code (ISO) and dial code
  // e.g., "India (IN) +91" instead of just "India"
  const customLabels = useMemo(() => {
    const labels = { ...en };
    const countries = getCountries();
    countries.forEach((countryCode) => {
      try {
        const callingCode = getCountryCallingCode(countryCode);
        const countryName = en[countryCode] || countryCode;
        labels[countryCode] = `${countryName} (${countryCode}) +${callingCode}`;
      } catch {
        // Keep the default label if calling code lookup fails
      }
    });
    return labels;
  }, []);

  return (
    <div className={`phone-input-wrapper ${hasError ? "has-error" : ""}`}>
      <PhoneInput
        international
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        labels={customLabels}
        id={id}
        name={name}
      />
    </div>
  );
}
